import { Auth0Client } from "@auth0/auth0-spa-js";
import axios from "axios";
import { auth0Config } from "../config";
import { Reference } from "../types/reference";
import { getBasicAuthConfig } from "./authUtils";
import store from "../store";

export class AIApi {
  private auth0Client: Auth0Client | null = null;

  constructor() {
    this.auth0Client = new Auth0Client({
      redirect_uri: window.location.origin,
      ...auth0Config,
    });
  }

  /** Prefer explicit countryCode; fall back to org country from the Redux store. */
  private getCountry(countryCode?: string): string | undefined {
    if (countryCode) return countryCode;
    const { currentOrganization, organization } = store.getState().user;
    return currentOrganization?.country || organization?.country;
  }

  /**
   * Send both header names for routing compatibility:
   * REST notes/speech/scribe use `x-country`; transcript WS uses `x-country-code`.
   */
  private countryHeader(countryCode?: string): Record<string, string> {
    const country = this.getCountry(countryCode);
    return country ? { "x-country": country, "x-country-code": country } : {};
  }

  async getTranscription(
    data: string,
    encounterId: string,
    providerReferece: Reference,
    countryCode?: string,
  ) {
    const authHeaders = await getBasicAuthConfig(this.auth0Client);
    const config = {
      headers: {
        ...authHeaders.headers,
        ...this.countryHeader(countryCode),
        "x-current-encounter": encounterId,
      },
    };

    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/speech/transcribe/b64`,
      { audio: data, attester: providerReferece },
      config,
    );
  }

  async generateNote(copilotId: string, countryCode: string) {
    const config = await getBasicAuthConfig(this.auth0Client);

    return await axios.get(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/notes/copilots/${copilotId}`,
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
        },
      },
    );
  }

  async healthCheck() {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_AI_SERVICE_BASE}/health`,
      );
      return response.data;
    } catch (e) {
      console.error(
        "AI Service health check was unsuccessful" + JSON.stringify(e),
      );
    }
  }

  async populateAllergies(
    encounterId: string,
    organization: string,
    countryCode?: string,
    options?: { overwriteReactions?: boolean },
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/allergies`,
      {
        overwriteReactions: options?.overwriteReactions,
      },
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
        },
      },
    );
  }

  async populateVitals(
    encounterId: string,
    organization: string,
    countryCode?: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/vitals`,
      {},
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
        },
      },
    );
  }

  async populateChiefComplaint(
    encounterId: string,
    organization: string,
    practitionerId: string,
    countryCode?: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/chief-complaint`,
      {},
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
          "x-current-provider": practitionerId,
        },
      },
    );
  }

  async populateHpi(
    encounterId: string,
    organization: string,
    practitionerId: string,
    countryCode?: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/hpi`,
      {},
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
          "x-current-provider": practitionerId,
        },
      },
    );
  }

  async populateFamilyHistory(
    encounterId: string,
    organization: string,
    countryCode?: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/family-history`,
      {},
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
        },
      },
    );
  }

  async populateHospitalizations(
    encounterId: string,
    organization: string,
    countryCode?: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/hospitalizations`,
      {},
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
        },
      },
    );
  }

  async populateMedicalHistory(
    encounterId: string,
    organization: string,
    countryCode?: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/medical-history`,
      {},
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
        },
      },
    );
  }

  async populateCarePlans(
    encounterId: string,
    organization: string,
    countryCode?: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/care-plans`,
      {},
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
        },
      },
    );
  }

  async populateImmunizations(
    encounterId: string,
    organization: string,
    countryCode?: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/immunizations`,
      {},
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
        },
      },
    );
  }

  async populateProcedures(
    encounterId: string,
    organization: string,
    countryCode?: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/procedures`,
      {},
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
        },
      },
    );
  }

  async populateDiagnoses(
    encounterId: string,
    organization: string,
    countryCode?: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/diagnoses`,
      {},
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
        },
      },
    );
  }

  async populateMedications(
    encounterId: string,
    organization: string,
    countryCode?: string,
  ) {
    const config = await getBasicAuthConfig(this.auth0Client);
    return await axios.post(
      `${import.meta.env.VITE_AI_SERVICE_BASE}/scribe/encounter/${encounterId}/medications`,
      {},
      {
        headers: {
          ...config.headers,
          ...this.countryHeader(countryCode),
          Organization: `Organization/${organization}`,
        },
      },
    );
  }
}

export const aiApi = new AIApi();
